import { type IOptions, type RecursivePartial } from '@tsparticles/engine';
import Layout from 'antd/es/layout';
import theme from 'antd/es/theme';
import { type ReactNode, Suspense, lazy, useEffect, useState } from 'react';
import styled from 'styled-components';

import ContentContainer from '@components/ContentContainer';

// Lazy load the particles components
const Particles = lazy(() => import('@tsparticles/react'));
const loadParticlesEngine = async () => {
  const { initParticlesEngine } = await import('@tsparticles/react');
  const { loadSlim } = await import('@tsparticles/slim');

  await initParticlesEngine(async (engine) => {
    await loadSlim(engine);
  });
};

const StyledLayout = styled(Layout)`
  height: 100vh;
  width: 100%;
`;

const ContentWrapper = styled.div<{ $bgColor: string }>`
  height: 100%;
  width: 100%;
  display: flex;
  overflow: auto;
`;

const InnerContent = styled.div`
  margin: auto;
  padding: 24px;
`;

const ParticlesWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 0;
  pointer-events: none;
`;

const UnauthenticatedLayout = ({ children }: { children: ReactNode }) => {
  const [init, setInit] = useState(false);
  const { token } = theme.useToken();

  useEffect(() => {
    void loadParticlesEngine().then(() => {
      setInit(true);
    });
  }, []);

  const particlesOptions = {
    particles: {
      number: {
        value: 100,
        density: {
          enable: true,
        },
      },
      color: {
        value: token.colorPrimary,
      },
      shape: {
        type: 'circle',
      },
      opacity: {
        value: 0.15,
      },
      size: {
        value: 2,
        animation: {
          enable: false,
        },
      },
      links: {
        enable: true,
        distance: 200,
        color: token.colorPrimary,
        opacity: 0.15,
        width: 1.5,
      },
      move: {
        enable: true,
        speed: 0.2,
        direction: 'none',
        random: false,
        straight: false,
        outModes: {
          default: 'out',
        },
        attract: {
          enable: false,
          rotate: {
            x: 600,
            y: 1200,
          },
        },
      },
    },
    interactivity: {
      events: {
        onHover: {
          enable: false,
          mode: 'repulse',
        },
        onClick: {
          enable: false,
          mode: 'push',
        },
      },
      modes: {
        repulse: {
          distance: 200,
          duration: 0.4,
        },
        push: {
          quantity: 4,
        },
      },
    },
    detectRetina: true,
  };

  return (
    <StyledLayout>
      {init && (
        <ParticlesWrapper>
          <Suspense fallback={null}>
            <Particles
              id="tsparticles"
              options={particlesOptions as RecursivePartial<IOptions>}
            />
          </Suspense>
        </ParticlesWrapper>
      )}
      <ContentWrapper $bgColor={token.colorBgContainer}>
        <InnerContent>{children}</InnerContent>
      </ContentWrapper>
    </StyledLayout>
  );
};

UnauthenticatedLayout.NotFound = () => {
  return (
    <UnauthenticatedLayout>
      <ContentContainer.NotFound />
    </UnauthenticatedLayout>
  );
};

export default UnauthenticatedLayout;
